import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowLeftIcon,
    ExclamationTriangleIcon,
    ClipboardDocumentCheckIcon,
    ClockIcon,
    GlobeAltIcon,
    ServerIcon,
    UserIcon,
} from "@heroicons/react/24/outline";

import { getAuditLogDetails } from "../api/auditApi";

// ============================================================================
// Helpers
// ============================================================================

const fmtDate = (d: string | null | undefined) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-NG", { dateStyle: "long", timeStyle: "medium" });
};

const fmtLabel = (s: string) =>
    s
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());

const statusColors: Record<string, string> = {
    SUCCESS: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    FAILURE: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    PARTIAL: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    DENIED: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const httpMethodColors: Record<string, string> = {
    GET: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    POST: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    PUT: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    PATCH: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    DELETE: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
};

// ============================================================================
// Component
// ============================================================================

export const AuditLogDetailsRoute = () => {
    const { logId } = useParams<{ logId: string }>();

    const { data, isLoading, error } = useQuery({
        queryKey: ["audit-log-details", logId],
        queryFn: () => getAuditLogDetails(logId!),
        enabled: !!logId,
    });

    const log = data?.data;

    // ────────────────────────────────────────────────────────────────────────
    // Loading / Error
    // ────────────────────────────────────────────────────────────────────────

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error || !log) {
        return (
            <div className="space-y-4">
                <Link
                    to="/audit-logs/logs"
                    className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline dark:text-primary-400"
                >
                    <ArrowLeftIcon className="h-4 w-4" /> Back to Logs
                </Link>
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                    <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                    <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">
                        Failed to load audit log details
                    </p>
                </div>
            </div>
        );
    }

    // ────────────────────────────────────────────────────────────────────────
    // Try to parse JSON strings for display
    // ────────────────────────────────────────────────────────────────────────

    const tryPrettyJson = (raw: string | null) => {
        if (!raw) return null;
        try {
            return JSON.stringify(JSON.parse(raw), null, 2);
        } catch {
            return raw;
        }
    };

    // ────────────────────────────────────────────────────────────────────────
    // Render
    // ────────────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6">
            {/* Back */}
            <Link
                to="/audit-logs/logs"
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline dark:text-primary-400"
            >
                <ArrowLeftIcon className="h-4 w-4" /> Back to Logs
            </Link>

            {/* Header */}
            <div className="flex flex-wrap items-start gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 dark:bg-primary-900/30">
                    <ClipboardDocumentCheckIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="flex-1">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                        {fmtLabel(log.action)}
                    </h2>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[log.status] ?? statusColors.SUCCESS
                                }`}
                        >
                            {log.status}
                        </span>
                        <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${httpMethodColors[log.httpMethod] ?? httpMethodColors.GET
                                }`}
                        >
                            {log.httpMethod}
                        </span>
                        <span className="text-xs text-slate-400">{fmtLabel(log.actionCategory)}</span>
                    </div>
                </div>
            </div>

            {/* Change Summary */}
            {log.changeSummary && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-1 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Summary
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300">{log.changeSummary}</p>
                </div>
            )}

            {/* Error message */}
            {log.errorMessage && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400">
                    <span className="font-medium">Error:</span> {log.errorMessage}
                </div>
            )}

            {/* Grid of Info Cards */}
            <div className="grid gap-6 lg:grid-cols-2">
                {/* Admin Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <UserIcon className="h-4 w-4 text-blue-500" /> Admin
                    </h3>
                    <dl className="space-y-2 text-sm">
                        {[
                            ["Email", log.adminEmail],
                            ["Role", log.adminRole],
                            ["Admin ID", log.adminId],
                        ].map(([label, value]) => (
                            <div key={label} className="flex justify-between">
                                <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                                <dd className="max-w-[200px] truncate font-medium text-slate-900 dark:text-white">
                                    {value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                {/* Target / Resource Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <ServerIcon className="h-4 w-4 text-amber-500" /> Resource
                    </h3>
                    <dl className="space-y-2 text-sm">
                        {[
                            ["Resource Type", log.resourceType],
                            ["Resource ID", log.resourceId],
                            ["Target User", log.targetUserEmail ?? log.targetUserId ?? "—"],
                            ["Target User Type", log.targetUserType ?? "—"],
                        ].map(([label, value]) => (
                            <div key={label} className="flex justify-between">
                                <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                                <dd className="max-w-[200px] truncate font-medium text-slate-900 dark:text-white">
                                    {value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                {/* Request Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <GlobeAltIcon className="h-4 w-4 text-emerald-500" /> Request
                    </h3>
                    <dl className="space-y-2 text-sm">
                        {[
                            ["Endpoint", log.endpoint],
                            ["Method", log.httpMethod],
                            ["Response Status", String(log.responseStatus)],
                            ["Duration", `${log.durationMs}ms`],
                            ["IP Address", log.ipAddress],
                        ].map(([label, value]) => (
                            <div key={label} className="flex justify-between">
                                <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                                <dd className="max-w-[200px] truncate font-medium text-slate-900 dark:text-white">
                                    {value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                {/* Session Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <ClockIcon className="h-4 w-4 text-purple-500" /> Session
                    </h3>
                    <dl className="space-y-2 text-sm">
                        {[
                            ["Performed At", fmtDate(log.performedAt)],
                            ["Session ID", log.sessionId ?? "—"],
                            ["Correlation ID", log.correlationId ?? "—"],
                        ].map(([label, value]) => (
                            <div key={label} className="flex justify-between">
                                <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                                <dd className="max-w-[200px] truncate font-medium text-slate-900 dark:text-white">
                                    {value}
                                </dd>
                            </div>
                        ))}
                        <div>
                            <dt className="mb-1 text-slate-500 dark:text-slate-400">User Agent</dt>
                            <dd className="break-all text-xs text-slate-600 dark:text-slate-300">
                                {log.userAgent || "—"}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            {/* Old / New Values */}
            {(log.oldValue || log.newValue) && (
                <div className="grid gap-6 lg:grid-cols-2">
                    {log.oldValue && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                            <h3 className="mb-2 text-sm font-semibold text-rose-600 dark:text-rose-400">
                                Old Value
                            </h3>
                            <pre className="max-h-60 overflow-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                {tryPrettyJson(log.oldValue)}
                            </pre>
                        </div>
                    )}
                    {log.newValue && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                            <h3 className="mb-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                                New Value
                            </h3>
                            <pre className="max-h-60 overflow-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                {tryPrettyJson(log.newValue)}
                            </pre>
                        </div>
                    )}
                </div>
            )}

            {/* Request Body */}
            {log.requestBody && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Request Body
                    </h3>
                    <pre className="max-h-60 overflow-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        {tryPrettyJson(log.requestBody)}
                    </pre>
                </div>
            )}
        </div>
    );
};
